'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Upload, Sparkles, ArrowRight, CheckCircle, Shield, Brain, Target, TrendingUp, MapPin, DollarSign, Wifi, Star, ChevronDown, ChevronUp, Zap, Clock, BarChart2, Trophy, Users, FileText, Bookmark, PenLine, Rocket } from 'lucide-react';
import { trackSignupStarted } from '@/lib/analytics';

// ─── Section 1: Hero ────────────────────────────────────────────────────────

function HeroSection() {
  const [pulse, setPulse] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setPulse(true);
      setTimeout(() => setPulse(false), 1200);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative overflow-hidden bg-[#F8FAFC] pt-10 pb-12 sm:pt-16 sm:pb-20">
      {/* Background depth — radial lavender glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-[#7C5CFF]/5 via-transparent to-[#8B5CF6]/3" />
        <div className="absolute top-[-40px] left-[-80px] w-[680px] h-[580px] rounded-full bg-[radial-gradient(ellipse_at_center,rgba(139,92,246,0.09)_0%,transparent_65%)] blur-3xl" />
        <div className="absolute top-[-60px] right-[-40px] w-[640px] h-[640px] rounded-full bg-[radial-gradient(ellipse_at_center,rgba(245,158,11,0.06)_0%,rgba(139,92,246,0.10)_35%,transparent_70%)] blur-3xl" />
        <div className="absolute bottom-[-40px] left-[-40px] w-[400px] h-[400px] rounded-full bg-[radial-gradient(ellipse_at_center,rgba(109,94,243,0.06)_0%,transparent_70%)] blur-3xl" />
      </div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">

          {/* ── LEFT SIDE ── */}
          <div className="flex flex-col items-start">
            {/* Badge */}
            <div className="mb-4">
              <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#7C5CFF]/8 border border-[#7C5CFF]/20 text-sm font-medium text-[#7C5CFF]">
                <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] animate-pulse" />
                Beta Access — Free During Beta
              </span>
            </div>

            {/* Headline */}
            <h1 className="text-[2.35rem] sm:text-[2.85rem] lg:text-[3rem] xl:text-[3.2rem] font-extrabold text-[#0F172A] leading-[1.05] tracking-tight max-w-[480px]">
              AI-Assisted Job Search.
              <br />
              <span className="text-[#475569]">You Stay in Control.</span>
              <br />
              <span className="bg-gradient-to-r from-[#7C5CFF] to-[#8B5CF6] bg-clip-text text-transparent">
                Every Step.
              </span>
            </h1>

            {/* Subheadline — honest positioning */}
            <p className="mt-4 text-base sm:text-lg text-slate-400 leading-[1.65] max-w-[440px]">
              Upload your resume. AI finds matching jobs and helps draft applications.
              You review everything before it goes anywhere.
            </p>

            {/* Feature status pills */}
            <div className="mt-5 flex flex-col gap-2">
              {[
                { label: 'Resume AI Analysis', status: 'active', color: 'text-[#10B981]', dot: 'bg-[#10B981]' },
                { label: 'AI Assist Apply (cover letters & drafts)', status: 'active', color: 'text-[#10B981]', dot: 'bg-[#10B981]' },
                { label: 'Auto-Apply — coming soon', status: 'soon', color: 'text-[#F59E0B]', dot: 'bg-[#F59E0B]' },
              ]?.map((item) => (
                <div key={item?.label} className="flex items-center gap-2.5">
                  <span className={`w-1.5 h-1.5 rounded-full ${item?.dot} shrink-0`} />
                  <span className="text-sm text-slate-600 font-medium">{item?.label}</span>
                  <span className={`text-xs font-semibold ${item?.color}`}>
                    {item?.status === 'active' ? '● Active' : '◌ Coming Soon'}
                  </span>
                </div>
              ))}
            </div>

            {/* CTAs */}
            <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full sm:w-auto">
              {/* Primary CTA */}
              <Link
                href="/sign-up-login-screen?tab=signup"
                onClick={() => trackSignupStarted('landing_hero')}
                className={`hero-cta-primary relative flex items-center gap-2.5 px-9 py-4 rounded-2xl bg-gradient-to-r from-[#7C5CFF] to-[#8B5CF6] text-white font-bold text-base shadow-[0_8px_32px_rgba(124,92,255,0.40)] hover:shadow-[0_12px_40px_rgba(124,92,255,0.55)] hover:-translate-y-0.5 hover:scale-[1.025] active:scale-[0.98] transition-all duration-200 cursor-pointer ${pulse ? 'ring-4 ring-[#7C5CFF]/30 ring-offset-2' : ''}`}
              >
                <Upload size={17} />
                Upload Resume — Start Free
                <span className="hero-cta-arrow transition-transform duration-200">
                  <ArrowRight size={15} />
                </span>
                {pulse && (
                  <span className="absolute inset-0 rounded-2xl animate-ping bg-[#7C5CFF]/20 pointer-events-none" />
                )}
              </Link>

              {/* Secondary ghost CTA */}
              <Link
                href="#how-it-works"
                className="flex items-center gap-2 px-7 py-4 rounded-2xl bg-white border border-[#E2E8F0] text-slate-600 font-semibold text-base hover:border-[#7C5CFF]/35 hover:text-[#7C5CF6] hover:-translate-y-0.5 transition-all duration-200 shadow-sm"
              >
                How It Works
                <ArrowRight size={14} />
              </Link>
            </div>

            {/* Social proof micro-line */}
            <p className="mt-3 text-xs text-slate-400 font-medium">
              Free during beta · No credit card required · You always review before applying
            </p>
          </div>

          {/* ── RIGHT SIDE — HUMAN IMAGE ── */}
          <div className="relative flex items-center justify-center lg:justify-end order-first lg:order-last">
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-[480px] h-[480px] rounded-full bg-[radial-gradient(ellipse_at_center,rgba(139,92,246,0.13)_0%,rgba(245,158,11,0.05)_45%,transparent_70%)] blur-2xl" />
            </div>

            <div className="relative w-full max-w-[480px]">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-[#7C5CFF]/15 border border-white/60" style={{ height: '520px' }}>
                <Image
                  src="/assets/images/hero_image-1778907536387.png"
                  alt="Excited woman in yellow sweater reacting with joy at laptop screen showing AI job matches"
                  fill
                  className="object-cover object-[center_15%]"
                  style={{ filter: 'brightness(0.92)', transform: 'scale(1.12)', transformOrigin: 'center 20%' }}
                  priority
                />
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(139,92,246,0.08)_0%,transparent_60%)] pointer-events-none" />
                <div className="absolute bottom-0 left-0 right-0 h-28 bg-gradient-to-t from-white/30 to-transparent pointer-events-none" />
              </div>

              {/* Live momentum indicator */}
              <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10">
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur-sm border border-[#E2E8F0] shadow-md">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] animate-pulse" />
                  <span className="text-[11px] font-semibold text-slate-600">AI analyzing profile…</span>
                </div>
              </div>

              {/* Floating Card 1 */}
              <div className="absolute -left-5 top-10 hero-float-slow group">
                <div className="bg-white/96 backdrop-blur-md rounded-2xl border border-[#7C5CFF]/25 shadow-2xl shadow-[#7C5CFF]/20 px-4 py-3.5 min-w-[168px] transition-transform duration-300 group-hover:-translate-y-1.5 group-hover:shadow-[0_16px_48px_rgba(124,92,255,0.28)]">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#7C5CFF] to-[#8B5CF6] flex items-center justify-center shadow-md shadow-[#7C5CFF]/30">
                      <Sparkles size={14} className="text-white" />
                    </div>
                    <span className="text-[10px] font-bold text-[#7C5CFF] uppercase tracking-wide">AI Matches</span>
                  </div>
                  <p className="text-[2rem] font-black text-[#0F172A] leading-none">179</p>
                  <p className="text-[11px] text-slate-500 mt-0.5">example opportunities</p>
                  <div className="mt-1.5 flex items-center gap-1">
                    <TrendingUp size={11} className="text-[#10B981]" />
                    <span className="text-[11px] font-bold text-[#10B981]">sample preview</span>
                  </div>
                </div>
              </div>

              {/* Floating Card 2 */}
              <div className="absolute -right-4 top-[18%] hero-float-medium group">
                <div className="bg-gradient-to-br from-[#7C5CFF] to-[#8B5CF6] rounded-2xl shadow-xl shadow-[#7C5CFF]/30 px-4 py-3 min-w-[136px] transition-transform duration-300 group-hover:-translate-y-1 opacity-95">
                  <p className="text-[11px] font-semibold text-white/80 mb-1">Example Match Score</p>
                  <p className="text-[1.75rem] font-black text-white leading-none">94%</p>
                  <div className="mt-2 w-full bg-white/20 rounded-full h-1.5">
                    <div className="bg-white rounded-full h-1.5 w-[94%]" />
                  </div>
                </div>
              </div>

              {/* Floating Card 3 */}
              <div className="absolute -left-3 bottom-[30%] hero-float-slow group hidden sm:block">
                <div className="bg-white/85 backdrop-blur-md rounded-2xl border border-[#E2E8F0] shadow-md shadow-slate-200/50 px-3.5 py-2.5 min-w-[130px] transition-transform duration-300 group-hover:-translate-y-0.5 opacity-80">
                  <div className="flex items-center gap-1.5 mb-1">
                    <div className="w-5 h-5 rounded-md bg-[#10B981]/10 flex items-center justify-center">
                      <CheckCircle size={10} className="text-[#10B981]" />
                    </div>
                    <span className="text-[10px] font-semibold text-[#10B981]">Good Fits</span>
                  </div>
                  <p className="text-xl font-extrabold text-[#0F172A]">41</p>
                  <p className="text-[10px] text-slate-400">Example High Potential Roles</p>
                </div>
              </div>

              {/* Floating Card 4 */}
              <div className="absolute -right-3 bottom-20 hero-float-medium group hidden sm:block">
                <div className="bg-white/85 backdrop-blur-md rounded-2xl border border-[#E2E8F0] shadow-md shadow-slate-200/50 px-3.5 py-2.5 min-w-[130px] transition-transform duration-300 group-hover:-translate-y-0.5 opacity-80">
                  <div className="flex items-center gap-1.5 mb-1">
                    <div className="w-5 h-5 rounded-md bg-[#7C5CFF]/10 flex items-center justify-center">
                      <Trophy size={10} className="text-[#7C5CFF]" />
                    </div>
                    <span className="text-[10px] font-semibold text-[#7C5CFF]">Strong Matches</span>
                  </div>
                  <p className="text-xl font-extrabold text-[#0F172A]">12</p>
                  <p className="text-[10px] text-slate-400">Example Ready To Apply</p>
                </div>
              </div>

              {/* Testimonial bubble */}
              <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 w-[calc(100%-24px)] max-w-[340px] hidden sm:block opacity-85">
                <div className="bg-white/90 backdrop-blur-md rounded-2xl border border-[#E2E8F0] shadow-lg shadow-slate-200/50 px-4 py-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[9px] font-semibold text-slate-400 uppercase tracking-wider">Illustrative Example</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#F59E0B] to-[#EF4444] flex items-center justify-center shrink-0 text-white text-xs font-bold">
                      S
                    </div>
                    <div>
                      <p className="text-[11px] text-slate-500 leading-relaxed italic">
                        &ldquo;I used to apply to 50+ jobs and hear nothing back. Now I actually know which jobs I have a chance at.&rdquo;
                      </p>
                      <p className="text-[10px] text-slate-400 mt-1 font-medium">Sarah K. · UX Designer</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Float animations */}
      <style jsx>{`
        @keyframes heroFloatSlow {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-7px); }
        }
        @keyframes heroFloatMedium {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-11px); }
        }
        .hero-float-slow { animation: heroFloatSlow 4.5s ease-in-out infinite; }
        .hero-float-medium { animation: heroFloatMedium 3.5s ease-in-out infinite; }
        .hero-cta-primary:hover .hero-cta-arrow { transform: translateX(4px); }
        .hero-cta-arrow { display: inline-flex; transition: transform 0.2s ease; }
      `}</style>
    </section>
  );
}

// ─── Section 1b: Honesty Banner ──────────────────────────────────────────────

function HonestyBanner() {
  return (
    <section className="bg-[#0F172A] border-y border-slate-800 py-6">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <p className="text-center text-xs font-semibold text-slate-400 uppercase tracking-widest mb-4">
          What&apos;s live today
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            {
              icon: FileText,
              title: 'Manual Apply',
              desc: 'You apply directly. We track it.',
              status: 'Active',
              statusColor: 'text-[#10B981] bg-[#10B981]/10 border-[#10B981]/20',
              dot: 'bg-[#10B981]',
            },
            {
              icon: PenLine,
              title: 'AI Assist',
              desc: 'AI drafts cover letters. You review & send.',
              status: 'Active',
              statusColor: 'text-[#10B981] bg-[#10B981]/10 border-[#10B981]/20',
              dot: 'bg-[#10B981]',
            },
            {
              icon: Rocket,
              title: 'Auto-Apply',
              desc: 'In safety testing. Coming soon.',
              status: 'Coming Soon',
              statusColor: 'text-[#F59E0B] bg-[#F59E0B]/10 border-[#F59E0B]/20',
              dot: 'bg-[#F59E0B]',
            },
          ]?.map((item) => {
            const ItemIcon = item?.icon;
            return (
              <div key={item?.title} className="flex items-start gap-3 bg-white/4 border border-white/8 rounded-xl px-4 py-3.5">
                <div className="w-8 h-8 rounded-lg bg-white/8 flex items-center justify-center shrink-0 mt-0.5">
                  <ItemIcon size={15} className="text-slate-300" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-bold text-white">{item?.title}</p>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${item?.statusColor}`}>
                      <span className={`w-1 h-1 rounded-full ${item?.dot}`} />
                      {item?.status}
                    </span>
                  </div>
                  <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">{item?.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
        <p className="text-center text-xs text-slate-500 mt-4">
          You always review AI-assisted applications before anything is submitted. We never apply without your approval.
        </p>
      </div>
    </section>
  );
}

// ─── Section 2: Trust Strip ──────────────────────────────────────────────────

function TrustStrip() {
  const items = [
    { icon: Brain, label: 'AI Resume Analysis' },
    { icon: Target, label: 'Smart Job Matching' },
    { icon: Zap, label: 'AI-Assisted Drafts' },
    { icon: Shield, label: 'You Always Review' },
  ];

  return (
    <section className="bg-white border-y border-slate-100 py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <div className="flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-10">
          {items?.map((item, i) => {
            const ItemIcon = item?.icon;
            return (
              <div key={i} className="flex items-center gap-2.5 text-slate-600">
                <div className="w-8 h-8 rounded-lg bg-[#6C63FF]/8 flex items-center justify-center">
                  <ItemIcon size={15} className="text-[#6C63FF]" />
                </div>
                <span className="text-sm font-medium">{item?.label}</span>
              </div>
            );
          })}
        </div>
        <p className="text-center text-xs text-slate-400 mt-5">
          Beta Access · AI-assisted, not fully automatic · You stay in control
        </p>
      </div>
    </section>
  );
}

// ─── Section 3: How It Works (5 honest steps) ────────────────────────────────

function HowItWorks() {
  const steps = [
    {
      num: '01',
      icon: Upload,
      title: 'Upload Your Resume',
      desc: 'Drop your resume in any format. AI reads it and builds your career profile in seconds.',
      color: 'bg-[#6C63FF]/8 text-[#6C63FF]',
      border: 'border-[#6C63FF]/15',
      badge: null,
    },
    {
      num: '02',
      icon: Sparkles,
      title: 'AI Finds Matching Jobs',
      desc: 'We scan thousands of jobs and surface only the ones where you have a realistic chance — ranked by fit.',
      color: 'bg-[#8B5CF6]/8 text-[#8B5CF6]',
      border: 'border-[#8B5CF6]/15',
      badge: null,
    },
    {
      num: '03',
      icon: Bookmark,
      title: 'Save & Track Applications',
      desc: 'Bookmark jobs you like. Track every application — status, notes, and timeline — all in one place.',
      color: 'bg-[#F59E0B]/8 text-[#F59E0B]',
      border: 'border-[#F59E0B]/15',
      badge: null,
    },
    {
      num: '04',
      icon: PenLine,
      title: 'AI Assists Your Drafts',
      desc: 'Get AI-crafted cover letters tailored to each job. You review and edit before anything is sent.',
      color: 'bg-[#10B981]/8 text-[#10B981]',
      border: 'border-[#10B981]/15',
      badge: { label: 'Active', color: 'text-[#10B981] bg-[#10B981]/10 border-[#10B981]/20' },
    },
    {
      num: '05',
      icon: Rocket,
      title: 'Auto-Apply (Coming Soon)',
      desc: 'We\'re safety-testing automated submission. When ready, you\'ll set rules and approve every batch.',
      color: 'bg-slate-100 text-slate-400',
      border: 'border-slate-100',
      badge: { label: 'Coming Soon', color: 'text-[#F59E0B] bg-[#F59E0B]/10 border-[#F59E0B]/20' },
    },
  ];

  return (
    <section id="how-it-works" className="py-20 bg-[#F8FAFC]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-12">
          <p className="text-sm font-semibold text-[#6C63FF] uppercase tracking-widest mb-3">How It Works</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
            From resume to tracked application in minutes
          </h2>
          <p className="mt-3 text-slate-500 text-base max-w-xl mx-auto">
            AI does the heavy lifting. You stay in control of every decision.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {steps?.slice(0, 3)?.map((step, i) => {
            const StepIcon = step?.icon;
            return (
              <div key={i} className={`relative bg-white rounded-2xl border ${step?.border} p-7 shadow-sm hover:shadow-md transition-shadow`}>
                <div className="flex items-start justify-between mb-5">
                  <div className={`w-12 h-12 rounded-xl ${step?.color} flex items-center justify-center`}>
                    <StepIcon size={22} />
                  </div>
                  <span className="text-3xl font-black text-slate-100">{step?.num}</span>
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{step?.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{step?.desc}</p>
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
          {steps?.slice(3)?.map((step, i) => {
            const StepIcon = step?.icon;
            return (
              <div key={i} className={`relative bg-white rounded-2xl border ${step?.border} p-7 shadow-sm hover:shadow-md transition-shadow`}>
                <div className="flex items-start justify-between mb-5">
                  <div className={`w-12 h-12 rounded-xl ${step?.color} flex items-center justify-center`}>
                    <StepIcon size={22} />
                  </div>
                  <div className="flex items-center gap-2">
                    {step?.badge && (
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${step?.badge?.color}`}>
                        {step?.badge?.label}
                      </span>
                    )}
                    <span className="text-3xl font-black text-slate-100">{step?.num}</span>
                  </div>
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{step?.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{step?.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ─── Section 4: AI Match Preview ─────────────────────────────────────────────

function MatchPreview() {
  const [expanded, setExpanded] = useState<number | null>(0);

  const matches = [
    {
      company: 'Stripe',
      role: 'Senior Frontend Engineer',
      location: 'Remote',
      salary: '$140k–$180k',
      label: 'Strong Match',
      labelColor: 'text-[#6C63FF] bg-[#6C63FF]/8 border-[#6C63FF]/20',
      emotionalLabel: 'High Response Potential',
      emotionalColor: 'text-[#6C63FF] bg-[#6C63FF]/8',
      why: 'Your 5 years of React experience and TypeScript skills align perfectly with this role. You\'ve worked on payment UIs before — Stripe will love that.',
      skills: ['React', 'TypeScript', 'Node.js'],
      isPremium: true,
    },
    {
      company: 'Linear',
      role: 'Product Engineer',
      location: 'San Francisco / Remote',
      salary: '$130k–$160k',
      label: 'Good Fit',
      labelColor: 'text-[#10B981] bg-[#10B981]/8 border-[#10B981]/20',
      emotionalLabel: 'Remote Friendly',
      emotionalColor: 'text-[#10B981] bg-[#10B981]/8',
      why: 'Your product-minded engineering background and experience with design systems makes you a strong candidate for this role.',
      skills: ['React', 'GraphQL', 'Design Systems'],
      isPremium: false,
    },
    {
      company: 'Vercel',
      role: 'Full Stack Developer',
      location: 'Remote',
      salary: '$120k–$155k',
      label: 'Good Fit',
      labelColor: 'text-[#10B981] bg-[#10B981]/8 border-[#10B981]/20',
      emotionalLabel: 'Good Salary Fit',
      emotionalColor: 'text-[#F59E0B] bg-[#F59E0B]/8',
      why: 'Your Next.js and deployment experience is directly relevant. Vercel values engineers who understand the full stack.',
      skills: ['Next.js', 'Vercel', 'PostgreSQL'],
      isPremium: false,
    },
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-12">
          <p className="text-sm font-semibold text-[#6C63FF] uppercase tracking-widest mb-3">AI Match Preview</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
            Jobs matched to <em className="not-italic bg-gradient-to-r from-[#6C63FF] to-[#8B5CF6] bg-clip-text text-transparent">your</em> profile
          </h2>
          <p className="mt-3 text-slate-500 text-base">Not just keyword matching — real AI reasoning about your fit. Every match comes with an explanation.</p>
        </div>

        <div className="space-y-3">
          {matches?.map((job, i) => (
            <div key={i} className={`bg-white rounded-2xl border overflow-hidden transition-all ${
              job?.isPremium
                ? 'border-[#6C63FF]/25 shadow-lg shadow-[#6C63FF]/8'
                : 'border-slate-100 shadow-sm'
            }`}>
              <div
                className="flex items-center gap-4 px-5 py-4 cursor-pointer hover:bg-slate-50/50 transition-colors"
                onClick={() => setExpanded(expanded === i ? null : i)}
              >
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center text-sm font-bold shrink-0 ${
                  job?.isPremium
                    ? 'bg-[#6C63FF]/10 border border-[#6C63FF]/20 text-[#6C63FF]'
                    : 'bg-gradient-to-br from-slate-100 to-slate-50 border border-slate-100 text-slate-600'
                }`}>
                  {job?.company?.slice(0, 2)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-bold text-slate-900">{job?.role}</p>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${job?.labelColor}`}>{job?.label}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${job?.emotionalColor}`}>{job?.emotionalLabel}</span>
                  </div>
                  <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                    <span className="text-xs text-slate-400">{job?.company}</span>
                    <span className="flex items-center gap-1 text-xs text-slate-400"><MapPin size={10} />{job?.location}</span>
                    <span className="flex items-center gap-1 text-xs font-semibold text-slate-700"><DollarSign size={10} />{job?.salary}</span>
                    <span className="flex items-center gap-1 text-xs text-[#10B981]"><Wifi size={10} />Remote</span>
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  {expanded === i ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
                </div>
              </div>

              {expanded === i && (
                <div className="px-5 pb-5 border-t border-slate-50">
                  <div className="mt-4 p-4 rounded-xl bg-[#6C63FF]/4 border border-[#6C63FF]/10">
                    <div className="flex items-start gap-2">
                      <Brain size={14} className="text-[#6C63FF] mt-0.5 shrink-0" />
                      <div>
                        <p className="text-xs font-semibold text-[#6C63FF] mb-1">Why AI matched this</p>
                        <p className="text-sm text-slate-600 leading-relaxed">{job?.why}</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-3 flex-wrap">
                    {job?.skills?.map((s, j) => (
                      <span key={j} className="px-2.5 py-1 rounded-full bg-[#6C63FF]/8 text-[#6C63FF] text-xs font-medium">{s}</span>
                    ))}
                  </div>
                  <div className="flex gap-2 mt-4">
                    <Link
                      href="/sign-up-login-screen"
                      onClick={() => trackSignupStarted('match_preview_cta')}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-gradient-to-r from-[#6C63FF] to-[#8B5CF6] text-white text-sm font-semibold hover:opacity-90 transition-opacity shadow-sm shadow-[#6C63FF]/20"
                    >
                      <Zap size={14} />
                      Prepare Application
                    </Link>
                    <button className="px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 text-sm font-medium hover:border-[#6C63FF]/30 transition-colors">
                      Save
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-8 text-center">
          <Link href="/sign-up-login-screen" className="inline-flex items-center gap-2 text-[#6C63FF] font-semibold text-sm hover:gap-3 transition-all">
            See all your matches after uploading your resume
            <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </section>
  );
}

// ─── Section 5: Testimonials ─────────────────────────────────────────────────

function Testimonials() {
  const testimonials = [
    {
      name: 'Sarah K.',
      role: 'UX Designer → Senior UX at Figma',
      avatar: 'SK',
      avatarBg: 'bg-[#6C63FF]/10 text-[#6C63FF]',
      quote: 'Found better remote roles in 2 days. The AI match explanations actually made sense — I knew exactly why each job was recommended.',
      stars: 5,
    },
    {
      name: 'Marcus T.',
      role: 'Backend Engineer → Staff Eng at Notion',
      avatar: 'MT',
      avatarBg: 'bg-[#10B981]/10 text-[#10B981]',
      quote: 'Much easier than manually searching job boards. The "why this matches you" section saved me hours of research.',
      stars: 5,
    },
    {
      name: 'Priya R.',
      role: 'Data Analyst → Senior PM at Stripe',
      avatar: 'PR',
      avatarBg: 'bg-[#8B5CF6]/10 text-[#8B5CF6]',
      quote: 'I was skeptical at first, but the AI actually understood my career pivot. Got 3 interviews in my first week.',
      stars: 5,
    },
  ];

  return (
    <section className="py-20 bg-[#F8FAFC]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-12">
          <p className="text-sm font-semibold text-[#6C63FF] uppercase tracking-widest mb-3">What Job Seekers Are Looking For</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
            Illustrative career outcomes
          </h2>
          <p className="mt-3 text-slate-400 text-sm max-w-xl mx-auto">
            Illustrative examples used to demonstrate how JobCompanion can support different career paths.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {testimonials?.map((t, i) => (
            <div key={i} className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-1 mb-4">
                {Array.from({ length: t?.stars })?.map((_, j) => (
                  <Star key={j} size={13} className="text-[#F59E0B] fill-[#F59E0B]" />
                ))}
              </div>
              <p className="text-sm text-slate-600 leading-relaxed mb-5">&ldquo;{t?.quote}&rdquo;</p>
              <div className="flex items-center gap-3">
                <div className={`w-9 h-9 rounded-full ${t?.avatarBg} flex items-center justify-center text-xs font-bold`}>
                  {t?.avatar}
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-800">{t?.name}</p>
                  <p className="text-xs text-slate-400">{t?.role}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Section 6: Why Users Love It ────────────────────────────────────────────

function WhyUsersLoveIt() {
  const cards = [
    {
      icon: Clock,
      title: 'Stop wasting hours applying',
      desc: 'AI filters out the noise. See only jobs where you have a real shot — not hundreds of irrelevant listings.',
      color: 'text-[#F59E0B] bg-[#F59E0B]/8',
    },
    {
      icon: Target,
      title: 'See better-fit opportunities',
      desc: 'Our matching engine considers your full profile — skills, experience, career goals, and salary expectations.',
      color: 'text-[#6C63FF] bg-[#6C63FF]/8',
    },
    {
      icon: Brain,
      title: 'Understand why jobs match you',
      desc: 'Every match comes with an AI explanation. Know exactly why you\'re a fit before you apply.',
      color: 'text-[#8B5CF6] bg-[#8B5CF6]/8',
    },
    {
      icon: BarChart2,
      title: 'Track everything in one place',
      desc: 'Applications, interviews, follow-ups — all organized and visible so nothing falls through the cracks.',
      color: 'text-[#10B981] bg-[#10B981]/8',
    },
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-12">
          <p className="text-sm font-semibold text-[#6C63FF] uppercase tracking-widest mb-3">Why It Works</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
            Built around your biggest frustrations
          </h2>
          <p className="mt-3 text-slate-500 text-base max-w-xl mx-auto">
            Avoiding blind applying. Reducing rejection. Finding better-fit jobs. Understanding why jobs match you.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {cards?.map((card, i) => {
            const CardIcon = card?.icon;
            return (
              <div key={i} className="flex gap-4 bg-[#F8FAFC] rounded-2xl border border-slate-100 p-6 hover:shadow-md transition-shadow">
                <div className={`w-11 h-11 rounded-xl ${card?.color} flex items-center justify-center shrink-0`}>
                  <CardIcon size={20} />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-900 mb-1.5">{card?.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{card?.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ─── Section 7: Final CTA ─────────────────────────────────────────────────────

function FinalCTA() {
  return (
    <section className="py-20 bg-gradient-to-br from-[#6C63FF] via-[#7C6FFF] to-[#8B5CF6]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/15 border border-white/20 text-sm font-medium text-white mb-6">
          <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] animate-pulse" />
          Free During Beta
        </div>

        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight tracking-tight">
          Your next opportunity might already match your resume.
        </h2>

        <p className="mt-5 text-lg text-white/80 leading-relaxed">
          Upload your resume and instantly discover jobs you realistically have a chance at.
          AI finds them. You choose. We help you prepare the application.
        </p>

        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link
            href="/sign-up-login-screen?tab=signup"
            onClick={() => trackSignupStarted('landing_final_cta')}
            className="flex items-center gap-2 px-8 py-4 rounded-xl bg-white text-[#6C63FF] font-bold text-base shadow-xl hover:shadow-2xl hover:scale-[1.02] active:scale-[0.98] transition-all"
          >
            <Upload size={18} />
            Upload Resume — Start Free
          </Link>
          <Link
            href="/sign-up-login-screen?tab=signup"
            onClick={() => trackSignupStarted('landing_final_cta_matches')}
            className="flex items-center gap-2 px-8 py-4 rounded-xl bg-white/10 border border-white/20 text-white font-semibold text-base hover:bg-white/20 transition-all"
          >
            <Sparkles size={16} />
            View My Matches
          </Link>
        </div>

        <div className="mt-6 flex items-center justify-center gap-6 text-sm text-white/60 flex-wrap">
          <span className="flex items-center gap-1.5"><CheckCircle size={13} className="text-white/70" />Free Beta Access</span>
          <span className="flex items-center gap-1.5"><CheckCircle size={13} className="text-white/70" />You always review before applying</span>
          <span className="flex items-center gap-1.5"><CheckCircle size={13} className="text-white/70" />Takes &lt; 2 minutes</span>
        </div>
      </div>
    </section>
  );
}

// ─── Section 8: Footer ────────────────────────────────────────────────────────

function Footer() {
  const productLinks = [
    { label: 'How It Works', href: '#how-it-works' },
    { label: 'Features', href: '#features' },
    { label: 'For Job Seekers', href: '#for-job-seekers' },
    { label: 'Dashboard', href: '/dashboard' },
  ];

  const resourceLinks = [
    { label: 'Legal Center', href: '/legal' },
    { label: 'Privacy Policy', href: '/privacy-policy' },
    { label: 'Terms & Conditions', href: '/terms-and-conditions' },
    { label: 'AI Disclosure', href: '/ai-disclosure' },
    { label: 'Auto-Apply Safety', href: '/auto-apply-safety' },
  ];

  const legalLinks = [
    { label: 'Legal Center', href: '/legal' },
    { label: 'Privacy Policy', href: '/privacy-policy' },
    { label: 'Terms', href: '/terms-and-conditions' },
    { label: 'Disclaimer', href: '/disclaimer' },
    { label: 'AI Disclosure', href: '/ai-disclosure' },
  ];

  return (
    <footer className="bg-[#F8FAFC]">
      {/* CTA strip */}
      <div className="border-t border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5 flex items-center justify-center">
          <p className="text-sm text-slate-500 font-medium tracking-wide">
            AI-assisted job search.{' '}
            <span className="text-slate-700 font-semibold">You stay in control.</span>
          </p>
        </div>
      </div>
      {/* Main footer */}
      <div className="border-t border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">

            {/* Column 1 — Brand */}
            <div className="sm:col-span-2 lg:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#6C63FF] to-[#8B5CF6] flex items-center justify-center">
                  <Sparkles size={13} className="text-white" />
                </div>
                <span className="font-bold text-slate-800 text-sm">JobCompanion</span>
                <span className="px-2 py-0.5 rounded-full bg-[#6C63FF]/8 text-[#6C63FF] text-[10px] font-semibold border border-[#6C63FF]/15">Beta</span>
              </div>
              <p className="text-sm text-slate-500 leading-relaxed mb-5 max-w-[220px]">
                AI-assisted job search that helps you find better-fit opportunities and prepare applications confidently.
              </p>
              <ul className="space-y-2">
                {[
                  'You stay in control',
                  'AI-assisted, not autonomous',
                  'Review before applying',
                ]?.map((item) => (
                  <li key={item} className="flex items-center gap-2 text-xs text-slate-400">
                    <span className="w-1 h-1 rounded-full bg-[#6C63FF]/50 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Column 2 — Product */}
            <div>
              <p className="text-xs font-semibold text-slate-800 uppercase tracking-widest mb-4">Product</p>
              <ul className="space-y-2.5">
                {productLinks?.map((link) => (
                  <li key={link?.href}>
                    <Link href={link?.href} className="text-sm text-slate-500 hover:text-slate-800 transition-colors">
                      {link?.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Column 3 — Resources */}
            <div>
              <p className="text-xs font-semibold text-slate-800 uppercase tracking-widest mb-4">Resources</p>
              <ul className="space-y-2.5">
                {resourceLinks?.map((link) => (
                  <li key={link?.href}>
                    <Link href={link?.href} className="text-sm text-slate-500 hover:text-slate-800 transition-colors">
                      {link?.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Column 4 — Legal */}
            <div>
              <p className="text-xs font-semibold text-slate-800 uppercase tracking-widest mb-4">Legal</p>
              <ul className="space-y-2.5">
                {legalLinks?.map((link) => (
                  <li key={link?.href}>
                    <Link
                      href={link?.href}
                      className={`text-sm transition-colors ${link?.href === '/legal' ? 'text-[#6C63FF] font-medium hover:text-[#5B52E0]' : 'text-slate-500 hover:text-slate-800'}`}
                    >
                      {link?.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
      {/* Bottom row */}
      <div className="border-t border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-slate-400">
          <p>© 2026 JobCompanion. All rights reserved.</p>
          <p>AI-assisted job search. You always review before applying.</p>
        </div>
      </div>
    </footer>
  );
}

// ─── Navbar ───────────────────────────────────────────────────────────────────

function Navbar() {
  return (
    <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-100">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#6C63FF] to-[#8B5CF6] flex items-center justify-center">
            <Sparkles size={13} className="text-white" />
          </div>
          <span className="font-bold text-slate-900 text-sm">JobCompanion</span>
          <span className="px-2 py-0.5 rounded-full bg-[#6C63FF]/8 text-[#6C63FF] text-xs font-semibold border border-[#6C63FF]/15">Beta</span>
        </div>

        <div className="flex items-center gap-2">
          <Link href="/sign-up-login-screen?tab=login" className="hidden sm:block px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">
            Sign In
          </Link>
          <Link
            href="/sign-up-login-screen?tab=signup"
            onClick={() => trackSignupStarted('navbar')}
            className="flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#7C5CFF] to-[#8B5CF6] text-white text-sm font-bold hover:shadow-[0_6px_20px_rgba(124,92,255,0.45)] hover:-translate-y-0.5 transition-all duration-200 shadow-[0_4px_14px_rgba(124,92,255,0.30)]"
          >
            <Upload size={14} />
            Upload Resume
          </Link>
        </div>
      </div>
    </nav>
  );
}

// ─── Root Page ────────────────────────────────────────────────────────────────

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <HeroSection />
      <HonestyBanner />
      <TrustStrip />
      <HowItWorks />
      <MatchPreview />
      <Testimonials />
      <WhyUsersLoveIt />
      <FinalCTA />
      <Footer />

      {/* Sticky mobile CTA */}
      <div className="fixed bottom-0 left-0 right-0 z-40 sm:hidden bg-white border-t border-slate-100 px-4 py-3 shadow-lg">
        <Link
          href="/sign-up-login-screen"
          onClick={() => trackSignupStarted('mobile_sticky_cta')}
          className="flex items-center justify-center gap-2 w-full py-3.5 rounded-xl bg-gradient-to-r from-[#6C63FF] to-[#8B5CF6] text-white font-bold text-sm shadow-lg shadow-[#6C63FF]/25"
        >
          <Upload size={16} />
          Upload Resume — Free Beta Access
        </Link>
      </div>
    </div>
  );
}
